/**
 * MissingDocumentsTab
 *
 * Missing documents from supervisees: Progress Notes, Treatment Plans,
 * Forms (ROI, Custody Agreement, Intake Form, Discharge Summary). Filters and table.
 */

import { Alert, Group, Loader, Paper, Stack } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MissingDocumentsFilters } from '../components/MissingDocumentsFilters';
import { MissingDocumentsTable } from '../components/MissingDocumentsTable';
import { SupervisionEmptyState } from '../components/SupervisionEmptyState';
import { TablePaginationFooter } from '@/components/TablePaginationFooter';
import { useMissingDocumentsFromMedplum } from '../hooks/useMissingDocumentsFromMedplum';
import type {
  MissingDocumentItem,
  MissingDocumentFilters as MissingDocumentFiltersType,
  MissingDocumentSortBy,
} from '../types';
import {
  defaultMissingDocumentFilters,
  getMissingDocumentRowTypeLabel,
  resolveMissingDocumentAppointmentId,
} from '../types';

export function MissingDocumentsTab(): JSX.Element {
  const navigate = useNavigate();
  const {
    items,
    supervisees,
    loading,
    error,
  } = useMissingDocumentsFromMedplum();
  const [filters, setFilters] = useState<MissingDocumentFiltersType>(
    defaultMissingDocumentFilters
  );

  const providerOptions = useMemo(
    () => supervisees.map((s) => ({ value: s.id, label: s.name })),
    [supervisees],
  );

  /** Only Overdue items show; Due Soon only for Intake Forms and Treatment Plans. */
  const filteredItems = useMemo(() => {
    let list = items.filter((i) => {
      if (i.status === 'overdue') return true;
      if (i.status === 'due-soon') {
        if (i.category === 'treatment-plans') return true;
        if (i.category === 'forms' && i.formType === 'intake') return true;
      }
      return false;
    });
    const q = filters.search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          (i.clinicianName ?? '').toLowerCase().includes(q) ||
          (i.clientName ?? '').toLowerCase().includes(q) ||
          (i.description ?? '').toLowerCase().includes(q) ||
          getMissingDocumentRowTypeLabel(i.category, i.formType).toLowerCase().includes(q)
      );
    }
    if (filters.providerIds.length > 0) {
      list = list.filter((i) => filters.providerIds.includes(i.clinicianId));
    }
    if (filters.types.length > 0) {
      list = list.filter((i) => filters.types.includes(i.category));
    }
    if (filters.types.includes('forms') && filters.formType !== 'all') {
      list = list.filter((i) => i.formType === filters.formType);
    }
    return list;
  }, [items, filters.search, filters.providerIds, filters.types, filters.formType]);

  const compareBy = useCallback(
    (a: MissingDocumentItem, b: MissingDocumentItem): number => {
      const { sortBy, sortDirection } = filters;
      const mult = sortDirection === 'asc' ? 1 : -1;
      let cmp = 0;
      switch (sortBy) {
        case 'client':
          cmp = (a.clientName ?? '').localeCompare(b.clientName ?? '');
          break;
        case 'sessionDate':
          cmp =
            new Date(a.sessionDate ?? 0).getTime() -
            new Date(b.sessionDate ?? 0).getTime();
          break;
        case 'dueDate':
          cmp =
            new Date(a.dueDate ?? 0).getTime() - new Date(b.dueDate ?? 0).getTime();
          break;
        case 'clinician':
          cmp = (a.clinicianName ?? '').localeCompare(b.clinicianName ?? '');
          break;
        case 'type':
          cmp = getMissingDocumentRowTypeLabel(a.category, a.formType).localeCompare(
            getMissingDocumentRowTypeLabel(b.category, b.formType),
          );
          break;
        case 'status':
          cmp = (a.status ?? '').localeCompare(b.status ?? '');
          break;
        default:
          break;
      }
      return mult * cmp;
    },
    [filters]
  );

  const sortedItems = useMemo(
    () => [...filteredItems].sort(compareBy),
    [filteredItems, compareBy]
  );

  const handleSort = useCallback((column: MissingDocumentSortBy) => {
    setFilters((prev) => {
      const sameColumn = prev.sortBy === column;
      if (sameColumn) {
        return { ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' };
      }
      return { ...prev, sortBy: column, sortDirection: 'asc' };
    });
  }, []);

  const handleRowClick = useCallback(
    (item: MissingDocumentItem) => {
      if (item.category === 'forms' || item.category === 'treatment-plans') {
        if (item.clientId) {
          navigate(`/dashboard/clients/${item.clientId}?tab=chart`);
        }
        return;
      }
      const apptId = resolveMissingDocumentAppointmentId(item);
      if (apptId) {
        navigate(`/dashboard/appointments/${apptId}`);
        return;
      }
      if (item.clientId) {
        navigate(`/dashboard/clients/${item.clientId}?tab=chart`);
      }
    },
    [navigate],
  );

  const showEmptyFilters =
    sortedItems.length === 0 &&
    (filters.search !== '' ||
      filters.providerIds.length > 0 ||
      filters.types.length > 0 ||
      filters.formType !== 'all');

  const showAllCaughtUp =
    sortedItems.length === 0 &&
    filters.search === '' &&
    filters.providerIds.length === 0 &&
    filters.types.length === 0 &&
    filters.formType === 'all';

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<string>('50');
  const itemsPerPage = parseInt(rowsPerPage || '50', 10);
  useEffect(() => {
    setCurrentPage(1);
  }, [sortedItems.length]);
  const paginatedItems = useMemo(
    () =>
      sortedItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [sortedItems, currentPage, itemsPerPage]
  );

  if (loading) {
    return (
      <Stack gap="md">
        <Group gap="md">
          <Loader size="sm" />
          <span>Loading missing documents…</span>
        </Group>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="md">
        <Alert color="red" title="Error loading missing documents">
          {error}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md" style={{ width: '100%' }}>
      <Group gap="md">
        <MissingDocumentsFilters
          filters={filters}
          onFiltersChange={setFilters}
          providerOptions={providerOptions}
        />
      </Group>

      <Paper p="lg" withBorder style={{ overflow: 'hidden', width: '100%' }}>
        {showAllCaughtUp ? (
          <SupervisionEmptyState variant="no-missing-documents" />
        ) : showEmptyFilters ? (
          <SupervisionEmptyState
            variant="no-results"
            onClearFilters={() => setFilters(defaultMissingDocumentFilters)}
          />
        ) : (
          <>
            <div style={{ overflow: 'auto', minHeight: 240, maxHeight: 'min(420px, 55vh)' }}>
              <MissingDocumentsTable
                items={paginatedItems}
                sortBy={filters.sortBy}
                sortDirection={filters.sortDirection}
                onSort={handleSort}
                onRowClick={handleRowClick}
              />
            </div>
            <TablePaginationFooter
              totalItems={sortedItems.length}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(v) => setRowsPerPage(v ?? '50')}
            />
          </>
        )}
      </Paper>
    </Stack>
  );
}
